"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { ShieldCheck, ShieldX, ChevronDown, ChevronUp } from "lucide-react";
import { decidePayoutApprovalAction } from "@/app/actions";
import type { PayoutApproval } from "@/lib/db";
import { fmtDateTime, statusPill } from "@/lib/format";

export default function ApprovalQueue({
  approvals,
  actingUser,
}: {
  approvals: PayoutApproval[];
  actingUser?: { email: string; role: string } | null;
}) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const mayApprove = !!actingUser && (actingUser.role === "approver" || actingUser.role === "admin");

  const open = approvals.filter((a) => a.status === "pending");
  const decided = approvals.filter((a) => a.status !== "pending");

  function decide(id: number, decision: "approve" | "reject") {
    setError(null);
    setNotice(null);
    start(async () => {
      const res = await decidePayoutApprovalAction(id, decision);
      if (res.ok) {
        setNotice(
          decision === "approve"
            ? `Approved — sent to Conduit${res.data.ref ? ` (${res.data.ref})` : ""}.`
            : "Rejected.",
        );
        router.refresh();
      } else setError(res.error);
    });
  }

  if (approvals.length === 0) return null;

  return (
    <section className="mb-10">
      <div className="mb-3 flex items-center justify-between">
        <div className="eyebrow" style={{ color: "var(--c-ink-soft)" }}>
          approvals
        </div>
        {decided.length > 0 && (
          <button
            type="button"
            className="flex items-center gap-1 text-[12px] font-medium underline-offset-2 hover:underline"
            style={{ color: "var(--c-ink-soft)" }}
            onClick={() => setShowHistory((s) => !s)}
          >
            {showHistory ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {showHistory ? "hide" : "show"} history ({decided.length})
          </button>
        )}
      </div>

      {open.length === 0 && (
        <p className="mb-4 text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
          Nothing waiting on your approval right now.
        </p>
      )}

      {open.length > 0 ? (
        <div className="card p-6">
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <p className="max-w-md text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
              Separation of duties: a payout only reaches Conduit once someone other than its initiator approves it.
            </p>
            <span className="pill pill-cream">
              {actingUser
                ? mayApprove
                  ? `approving as ${actingUser.email}`
                  : `${actingUser.email} · '${actingUser.role}' cannot approve`
                : "select who you're acting as (top-left)"}
            </span>
          </div>
          <table className="table">
            <thead>
              <tr>
                <th>payment</th>
                <th>initiated by</th>
                <th>requested</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {open.map((a) => (
                <tr key={a.id}>
                  <td className="font-normal">{a.summary}</td>
                  <td className="text-[13px]">{a.initiated_by}</td>
                  <td>{fmtDateTime(a.created_at)}</td>
                  <td>
                    <div className="flex justify-end gap-2">
                      <button
                        className="btn btn-primary !px-4 !py-1.5 text-[13px]"
                        disabled={pending || !mayApprove}
                        onClick={() => decide(a.id, "approve")}
                      >
                        <ShieldCheck size={13} /> Approve & send
                      </button>
                      <button
                        className="btn btn-ghost !px-4 !py-1.5 text-[13px]"
                        disabled={pending || !mayApprove}
                        onClick={() => decide(a.id, "reject")}
                      >
                        <ShieldX size={13} /> Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {notice && <p className="mt-3 text-[13px] font-light">{notice}</p>}
          {error && (
            <p className="mt-3 text-[13px] font-light" style={{ color: "var(--c-danger)" }}>
              {error}
            </p>
          )}
        </div>
      ) : null}

      {showHistory && decided.length > 0 && (
        <div className="card mt-4 overflow-hidden">
          <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>payment</th>
                <th>initiated by</th>
                <th>decided by</th>
                <th>status</th>
                <th>result</th>
              </tr>
            </thead>
            <tbody>
              {decided.map((a) => (
                <tr key={a.id}>
                  <td className="font-normal">{a.summary}</td>
                  <td className="text-[13px]">{a.initiated_by}</td>
                  <td className="text-[13px]">{a.approved_by ?? "—"}</td>
                  <td>
                    <span className={statusPill(a.status)}>{a.status}</span>
                  </td>
                  <td className="mono text-[12px]">{a.result_ref ?? a.failure ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </section>
  );
}
